import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpParams } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import * as fs from 'file-saver';
import * as Sentry from '@sentry/angular';
import { ConfigService } from './config.service';
import { BaseHttpService } from './base-http.service';
import { RtsGeneratorService } from './docs-service/rts-generator.service';
import { FilesHelperService } from './files-helper.service';

export enum NodeType {
  GROUP = 'GROUP',
  DOCUMENT = 'DOCUMENT',
  VEHICLE = 'VEHICLE',
  DATE = 'DATE'
}

/** Каталог типов документов сделки (шаблоны, подписанные версии, реестры, накладные и т.п.). */
export enum DocTypes {
  SPECIAL_TERMS = 'SPECIAL_TERMS',
  TEST_REPORT = 'TEST_REPORT',
  DECLARATION = 'DECLARATION',
  SIGNED_SPECIFICATION = 'SIGNED_SPECIFICATION',
  SPECIFICATION_TEMPLATE = 'SPECIFICATION_TEMPLATE',
  PAYMENT_ORDER = 'PAYMENT_ORDER',
  INVOICE = 'INVOICE',
  REFUND_REQUIREMENT = 'REFUND_REQUIREMENT',
  UNDERPAYMENT_INVOICE = 'UNDERPAYMENT_INVOICE',
  TORG_12_TEMPLATE = 'TORG_12_TEMPLATE',
  SIGNED_TORG_12 = 'SIGNED_TORG_12',
  UPD_TEMPLATE = 'UPD_TEMPLATE',
  SIGNED_UPD = 'SIGNED_UPD',
  SERVICE_FEE_INVOICE = 'SERVICE_FEE_INVOICE',
  PREPAYMENT_INVOICE_TEMPLATE = 'PREPAYMENT_INVOICE_TEMPLATE',
  SIGNED_PREPAYMENT_INVOICE = 'SIGNED_PREPAYMENT_INVOICE',
  TRUNCATED_RTS = 'TRUNCATED_RTS',
  FORWARDER_RECEIPT = 'FORWARDER_RECEIPT',
  FORWARDER_FINAL_DOCS = 'FORWARDER_FINAL_DOCS',
  REGISTER_OF_SHIPPED = 'REGISTER_OF_SHIPPED',
  DAILY_UPD = 'DAILY_UPD',
  FORWARDER_TTN = 'FORWARDER_TTN',
  FORWARDER_TRN = 'FORWARDER_TRN',
  UNDERPAYMENT_INVOICE_TEMPLATE = 'UNDERPAYMENT_INVOICE_TEMPLATE',
  REFUND_REQUIREMENT_TEMPLATE = 'REFUND_REQUIREMENT_TEMPLATE',
  SHIPPING_LETTER_TEMPLATE = 'SHIPPING_LETTER_TEMPLATE',
  SIGNED_SHIPPING_LETTER = 'SIGNED_SHIPPING_LETTER',
  AGENCY_AGREEMENT = 'AGENCY_AGREEMENT',
  REGISTER_OF_ACCEPTED = 'REGISTER_OF_ACCEPTED',
  DAILY_REF_FOR_GOODS_QUALITY = 'DAILY_REF_FOR_GOODS_QUALITY',
  FORWARDER_ASSIGNMENT_TEMPLATE = 'FORWARDER_ASSIGNMENT_TEMPLATE',
  SIGNED_FORWARDER_ASSIGNMENT = 'SIGNED_FORWARDER_ASSIGNMENT',
  TRANSPORTATION_CONTRACT = 'TRANSPORTATION_CONTRACT',
  DAILY_UPD_TEMPLATE = 'DAILY_UPD_TEMPLATE',
  SIGNED_DAILY_UPD = 'SIGNED_DAILY_UPD',
  FORWARDING_OTHER_DOC = 'FORWARDING_OTHER_DOC'
}

export interface GetFileTreeResponse {
  success: boolean;
  data: {
    nodes: FileNode[];
  };
}

export interface FileNode {
  nodeType: NodeType;
  name: string;
  documentId?: number;
  items?: FileNode[];
  isManualLoaded?: boolean;
  isVerified?: boolean;
  fromEdo?: boolean;
  hasBES?: boolean;
  documentType?: DocTypes;
}

/**
 * Загрузка и сохранение документов сделки на всех платформах. Два пути:
 * - РТС ({@code TRUNCATED_RTS}) формируется ЛОКАЛЬНО генератором (он сам сохраняет и открывает файл),
 *   а не качается через API;
 * - прочие документы качаются с сервера как blob и сохраняются: в web — скачиванием браузера
 *   ({@code saveAs}), на iOS/Android — записью в Documents ({@code Filesystem}).
 */
@Injectable({
  providedIn: 'root'
})
export class FileService extends BaseHttpService {
  constructor(
    http: HttpClient,
    config: ConfigService,
    private rtsGeneratorService: RtsGeneratorService,
    private filesHelper: FilesHelperService
  ) {
    super(http, config);
  }

  /**
   * Готовит документ сделки к открытию. Возвращает ПУТЬ к сохранённому файлу, по которому вызывающий
   * затем открывает его на нативе (условие потребителей — {@code if (path && isNativePlatform())}):
   * - для РТС возвращает ПУСТУЮ строку (генератор уже сохранил и открыл файл сам) — чтобы вызывающий
   *   не открыл документ повторно по несуществующему пути (дефект A1, см. пояснение ниже);
   * - в web возвращает имя файла (скачан браузером), на нативе — санитизированный путь в Documents.
   */
  public async downloadFileForDeal(
    documentId: number,
    fileName: string,
    documentType?: DocTypes,
    orderId?: number,
    shipmentDate?: string
  ): Promise<string> {
    // РТС формируем локально, а не качаем через API. Генератор сам сохраняет файл и открывает его
    // (на нативе — Filesystem + openFile, в web — saveAs), поэтому возвращаем ПУСТУЮ строку: все
    // потребители открывают файл лишь при непустом пути (`if (filePath && isNativePlatform())`), и
    // пустая строка не даёт им открыть документ ПОВТОРНО по отображаемому имени (несуществующему
    // пути) — прежде это на iOS/Android приводило к ложной ошибке «Не удалось открыть файл».
    if (documentType === DocTypes.TRUNCATED_RTS) {
      if (orderId && shipmentDate) {
        try {
          await this.rtsGeneratorService.getTtnByShipmentDate(orderId, shipmentDate);
          return '';
        } catch (error) {
          Sentry.captureException(error);
          throw new Error('Не удалось сгенерировать РТС документ по дате отгрузки');
        }
      }
    }

    const params = new HttpParams().set('documentId', documentId);

    try {
      const response = await lastValueFrom(
        this.http.get(`${this.baseRestURL}order/bargain/document`, {
          responseType: 'blob',
          params,
          observe: 'events',
          reportProgress: true
        })
      );

      if (
        !response ||
        response.type !== HttpEventType.Response ||
        !response.body
      ) {
        throw new Error('Файл не найден');
      }

      const fileBlob = new Blob([response.body], {
        type: response.headers.get('Content-Type') || undefined
      });

      // Кроссплатформенное сохранение: web — скачивание браузером; натив — запись blob в Documents
      // (имя санитизируем; возвращаемый путь потребитель откроет через FilesHelper.openFile).
      if (Capacitor.getPlatform() === 'web') {
        fs.saveAs(fileBlob, fileName);
        return fileName;
      } else {
        const base64Data = await this.filesHelper.convertBlobToBase64(fileBlob);
        if (!base64Data) {
          throw new Error('Ошибка конвертации файла');
        }

        const sanitizedFileName = this.filesHelper.sanitizeFileName(fileName);
        const config = {
          directory: Directory.Documents,
          path: sanitizedFileName,
          recursive: true
        };

        await Filesystem.writeFile({
          ...config,
          data: base64Data
        });

        return sanitizedFileName;
      }
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  /** Дерево документов сделки с сервера (для экрана списка документов заявки). */
  public getDealFiles(bargainId: number): Observable<GetFileTreeResponse> {
    const curUrl = `${this.baseRestURL}order/bargain/${bargainId}/documents/tree`;
    return this.http.get<GetFileTreeResponse>(curUrl);
  }
}
